<template>
  <div class="page-container">
    <div class="import-export-title primary-color import-export-title-margins">
      <v-icon color="secondary" class="arrow-back" @click="goBack()"> arrow_back </v-icon>
      <span style="margin-left: 16px">{{ workflowScreenTitle(screenTitle) }}</span>
    </div>
    <div class="import-export-container import-export-container-margins">
      <module-title
        color="#0277BD"
        class="import-export-module-title-margins"
        :title="adminText('workflowsAll.1_create_the_csv_file_78f6567', '1. Create the CSV file')"
      />
      <div class="import-export-description-margins">
        {{ adminText('workflowsAll.first_you_need_to_download_the_official_csv_t_58bc94b', 'First, you need to download the official CSV template file which contains all of the attendee fields you can fill out. Make sure you enter all of the required fields for all of your attendees to make it valid.') }}
      </div>
      <div class="import-export-description-margins">
        {{ adminText('workflowsAll.your_template_contains_following_fields_8cc62c3', 'Your template contains following fields:') }}<br>
        <span
          v-for="field in fields"
          :key="field.name"
          class="import-field-list"
        >{{ `• ${field.name} ${workflowImportRequirement(field.required)}` }}<br></span>
      </div>
      <div class="import-export-buttons">
        <a :href="`${serverURL}/files/events/${eventId}/export/template/csv/${token}`">
          <main-button depressed :text="adminText('workflowsAll.download_template_d268565', 'Download template')" />
        </a>
      </div>
    </div>
    <div class="import-export-container import-export-container-margins">
      <module-title
        color="#0277BD"
        class="import-export-module-title-margins"
        :title="adminText('workflowsAll.2_upload_the_csv_file_d3cf894', '2. Upload the CSV file')"
      />
      <div class="import-export-description-margins">
        {{ adminText('workflowsAll.after_you_finish_adding_the_attendees_data_yo_f76061c', 'After you finish adding the attendees data, you can upload the file here. Don\'t worry, if something goes wrong, we will let you know.') }}
      </div>
      <div class="import-export-buttons">
        <main-button depressed :text="adminText('workflowsAll.upload_file_503a3d8', 'Upload file')" @click="openFSPicker" />
      </div>
      <hidden-file-input
        ref="file-input"
        accept="text/csv .csv"
        max-file-size-mb="10"
        @file="processFile($event)"
      />
    </div>
    <success-snackbar
      :open="importStatus == 'success'"
      :title="adminText('workflowsAll.success_42a8f65', 'Success')"
      :message="adminText('workflowsAll.all_attendees_were_successfully_imported_2cae616', 'All attendees were successfully imported.')"
    />
    <errors-snackbar
      :open="importStatus == 'error'"
      :errors="importErrors"
      :title="adminText('workflowsAll.error_7f2f6a1', 'Error')"
      :message="adminText('workflowsAll.we_encountered_errors_during_validation_proce_379150d', 'We encountered errors during validation process.')"
    />
    <progress-dialog :open="importStatus == 'progress'" :message="adminText('workflowsAll.importing_attendees_d087290', 'Importing attendees...')" />
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { serverURL, objectToArray } from '../../utils.js'
import { uploadAttendeeCSV } from '../../http.js'
import HiddenFileInput from './../shared/hidden-file-input'
import ModuleTitle from './../shared/module-title.vue'
import ProgressDialog from './../shared/dialogs/progress-dialog'
import SuccessSnackbar from './../shared/snackbars/success-snackbar'
import ErrorsSnackbar from './../shared/snackbars/errors-snackbar'
import { getExportToken } from '../../http'
import { GET_EVENT_SETTINGS } from '../../graphql/event.graphql'

export default {
  mixins: [WorkflowDisplay],
  name: 'import-container',
  components: {
    HiddenFileInput,
    ModuleTitle,
    ProgressDialog,
    SuccessSnackbar,
    ErrorsSnackbar,
  },
  props: {
    eventId: {
      default: null,
      type: String,
    },
    screenTitle: {
      default: 'Import attendees',
      type: String,
    },
    hiddenCustomField: {
      default: () => {},
      type: Object,
    },
  },
  data() {
    return {
      serverURL,
      token: undefined,
      importStatus: undefined,
      importErrors: [],
      fields: [],
    }
  },
  async created() {
    const { data } = await getExportToken(this.eventId)
    if (!data) {
      // handle
    }

    this.token = data.token
  },
  methods: {
    async processFile(file) {
      this.importStatus = 'progress'
      const importCuratedInvitees = this.hiddenCustomField && this.hiddenCustomField.curatedInvitee
      const {
        data: { errors },
      } = await uploadAttendeeCSV(this.eventId, file, this.token, importCuratedInvitees)
      if (errors) {
        this.importErrors = errors
        this.importStatus = 'error'
      } else {
        this.importStatus = 'success'
      }
    },
  },
  apollo: {
    getEventConferenceData: {
      query: GET_EVENT_SETTINGS,
      variables() {
        return {
          id: this.eventId,
        }
      },
      manual: true,
      result({ data: { getEvent: event }, loading }) {
        if (!loading) {
          const { attendeeFields, extraModules } = event
          const fieldsList = objectToArray(attendeeFields, 'name')
          this.fields = fieldsList.map(f => {
            return {
              name: f.name,
              required: f.validations.required ? '(required)' : '',
            }
          })
          if (extraModules.categoriesAndZones) {
            this.fields.push({
              name: 'Category',
              required: '(required)',
            })
            this.fields.push({
              name: 'Zones',
              required: '(required) - eg. 1-2-3',
            })
          }
        }
      },
    },
  },
}
</script>

<style>
.arrow-back {
  cursor: pointer;
  margin-bottom: 2px;
}

.import-export-container {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding-top: 1.5rem;
  max-width: 676px !important;
}

.import-export-title-margins {
  margin-top: 0;
}

.import-export-module-title-margins {
  margin-bottom: 0.75rem;
}

.import-export-description-margins {
  text-align: left;
  margin-right: 2.25rem;
  margin-bottom: 1.5rem;
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #666666;
}

.import-export-buttons {
  margin-bottom: 32px;
}

@media (max-width: 800px) {
  .import-export-title-margins,
  .import-export-title,
  .import-export-description-margins,
  .import-export-buttons,
  .import-export-module-title-margins {
    margin-left: 0;
    margin-right: 0;
  }

  .import-export-container {
    padding: 1.5rem 24px 0;
  }
}

.import-export-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
}

.import-export-container-margins {
  padding-left: 2.25rem;
}

.page-container {
  display: flex;
  row-gap: 1.5rem;
  flex-direction: column;
}

.import-field-list {
  padding-left: 0.625rem;
}

.import-export-page-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
}
</style>
